import { adminDiscountCoupon, adminDiscountOrder } from '@sparklayer-io/spark-dash-ui/assets';
import { Button, Card, Divider, EmptyState, ExternalLink, MediaCard, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { IconCard } from '../../../components/icon-card';
import { SparkSuspense } from '../../../components/spark-suspense';
import { featureRestricted, features, getActiveBillingPlanKey } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import { formatMoney } from '../../../utils/format-money';
import { DiscountsTable } from '../components/discounts-table';
import { DiscountsUpsell } from '../components/discounts-upsell';
import { useDiscountsList } from '../hooks/use-discounts-list';
import type { Discount } from '../types';

export const DiscountsListPage = withPageProviders(DiscountsListPageContent);

export const DISCOUNTS_LEARN_MORE_URL = 'https://docs.sparklayer.io/discounts';

function sortDiscounts(a: Discount, b: Discount) {
  return b.priority - a.priority || a.internal_name.localeCompare(b.internal_name);
}

type DiscountsListPageProps = {
  onCreateDiscount: (discounts: Discount[]) => void;
  onCreateDiscountFromTemplate: (template: Partial<Discount>, discounts: Discount[]) => void;
  onEditDiscount: (discount: Discount, discounts: Discount[]) => void;
  onManagePriorities: (priorityDiscounts: Discount[]) => void;
  onDiscountsChanged: (refresh: () => void) => void;
};

function DiscountsListPageContent({
  onCreateDiscount,
  onCreateDiscountFromTemplate,
  onEditDiscount,
  onManagePriorities,
  onDiscountsChanged,
}: DiscountsListPageProps) {
  const { navigationHandler } = useHandlers();
  const env = useEnv();

  const needsUpgrade = featureRestricted(
    features.DISCOUNTS,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const {
    data: discounts,
    isLoading: discountsLoading,
    isError: discountsError,
    refetch: refetchDiscounts,
  } = useDiscountsList();

  useEffect(() => {
    onDiscountsChanged(() => {
      void refetchDiscounts();
    });
  }, [onDiscountsChanged, refetchDiscounts]);

  const { baseCurrency } = useSettings();

  const sortedDiscounts = useMemo(() => [...discounts].sort(sortDiscounts), [discounts]);

  if (needsUpgrade) {
    return (
      <Page
        title={t('features:discounts.pages.discounts-list-page.header.title')}
        description={t('features:discounts.pages.discounts-list-page.header.description')}
        learnMoreUrl={DISCOUNTS_LEARN_MORE_URL}
      >
        <DiscountsUpsell />
      </Page>
    );
  }

  return (
    <Page
      title={t('features:discounts.pages.discounts-list-page.header.title')}
      description={t('features:discounts.pages.discounts-list-page.header.description')}
      learnMoreUrl={DISCOUNTS_LEARN_MORE_URL}
      primaryAction={{
        onClick: () => onCreateDiscount(sortedDiscounts),
        label: t('features:discounts.pages.discounts-list-page.header.actions.create'),
      }}
      secondaryActions={
        sortedDiscounts.length > 1
          ? [
              {
                onClick: () => onManagePriorities(sortedDiscounts),
                label: t('features:discounts.pages.discounts-list-page.header.actions.prioritise'),
              },
            ]
          : undefined
      }
    >
      <SparkSuspense
        isError={discountsError}
        isLoading={discountsLoading}
        errorMessage={t('errors:generic')}
        onReload={() => void refetchDiscounts()}
      >
        {sortedDiscounts.length > 0 ? (
          <div>
            <DiscountsTable
              title={t('features:discounts.components.list_table.caption')}
              discounts={sortedDiscounts}
              onEditDiscount={onEditDiscount}
              onCreateDiscountFromTemplate={onCreateDiscountFromTemplate}
            />
          </div>
        ) : (
          <Card>
            <EmptyState
              title={t('features:discounts.pages.discounts-list-page.empty.title')}
              subtitle={t('features:discounts.pages.discounts-list-page.empty.body')}
              imageType="discount"
            >
              <div className="flex flex-col items-center gap-4">
                <Button icon="document" onClick={() => onCreateDiscount(sortedDiscounts)}>
                  {t('features:discounts.pages.discounts-list-page.empty.actions.create')}
                </Button>
                <Text variant="secondary">
                  {t('features:discounts.pages.discounts-list-page.empty.suggestions.title')}
                </Text>

                <div className="w-full">
                  <Divider />
                </div>

                <IconCard
                  title={t(
                    'features:discounts.pages.discounts-list-page.empty.suggestions.percentage.title',
                  )}
                  subtitle={t(
                    'features:discounts.pages.discounts-list-page.empty.suggestions.percentage.description',
                  )}
                  icon="percentage"
                  onClick={() =>
                    onCreateDiscountFromTemplate(
                      {
                        template: 'order_level_get_amount_off',
                        internal_name: '20% off with coupon code 20-OFF',
                        internal_slug: '20-off-with-coupon',
                        name: t(
                          'features:discounts.pages.discounts-list-page.empty.suggestions.percentage.title',
                        ),
                        other_requirements: [
                          {
                            requirement_type: 'coupons',
                            coupons: [
                              {
                                code: '20-OFF',
                              },
                            ],
                          },
                        ],
                        rewards: [
                          {
                            type: 'order',
                            amount_type: 'percent',
                            amount: 20,
                            item_id: 'Not applicable',
                          },
                        ],
                      },
                      sortedDiscounts,
                    )
                  }
                />

                <IconCard
                  title={t(
                    'features:discounts.pages.discounts-list-page.empty.suggestions.fixed.title',
                    {
                      discount: baseCurrency ? formatMoney(100, baseCurrency) : '100',
                      amount: baseCurrency ? formatMoney(1000, baseCurrency) : '1,000',
                    },
                  )}
                  subtitle={t(
                    'features:discounts.pages.discounts-list-page.empty.suggestions.fixed.description',
                  )}
                  icon="money"
                  onClick={() =>
                    onCreateDiscountFromTemplate(
                      {
                        template: 'order_level_get_amount_off',
                        internal_name: '100 off on orders over 1000',
                        internal_slug: '100-off-on-orders-over-1000',
                        name: t(
                          'features:discounts.pages.discounts-list-page.empty.suggestions.fixed.name',
                        ),
                        currency_code: baseCurrency,
                        other_requirements: [
                          {
                            requirement_type: 'spendAmount',
                            tax_type: 'net',
                            min: 1000,
                          },
                        ],
                        rewards: [
                          {
                            type: 'order',
                            amount_type: 'priceAllNet',
                            amount: 100,
                            item_id: 'Not applicable',
                          },
                        ],
                      },
                      sortedDiscounts,
                    )
                  }
                />

                <ExternalLink
                  link="https://docs.sparklayer.io/discounts#types-of-discounts-available"
                  showIcon
                >
                  {t('features:discounts.pages.discounts-list-page.empty.learn_more')}
                </ExternalLink>
              </div>
            </EmptyState>
          </Card>
        )}
      </SparkSuspense>

      <div className="flex flex-row gap-4">
        <div className="flex-1">
          <MediaCard
            imgSrc={adminDiscountCoupon}
            imgFit="cover"
            title={t('features:discounts.pages.discounts-list-page.help.setup.title')}
            headingLevel="h2"
            description={t('features:discounts.pages.discounts-list-page.help.setup.body')}
            action={{
              content: t('features:discounts.pages.discounts-list-page.help.setup.action'),
              icon: 'externalLink',
              onClick: () =>
                navigationHandler.navigate('https://docs.sparklayer.io/discounts#coupon-codes', {
                  absolute: true,
                  newTab: true,
                }),
            }}
          />
        </div>
        <div className="flex-1">
          <MediaCard
            imgSrc={adminDiscountOrder}
            imgFit="cover"
            title={t('features:discounts.pages.discounts-list-page.help.configure.title')}
            headingLevel="h2"
            description={t('features:discounts.pages.discounts-list-page.help.configure.body', {
              amount: baseCurrency ? formatMoney(1000, baseCurrency) : '1,000',
            })}
            action={{
              content: t('features:discounts.pages.discounts-list-page.help.configure.action'),
              icon: 'externalLink',
              onClick: () =>
                navigationHandler.navigate(
                  'https://docs.sparklayer.io/discounts#order-sub-total-limits',
                  {
                    absolute: true,
                    newTab: true,
                  },
                ),
            }}
          />
        </div>
      </div>
    </Page>
  );
}
